import { useValue } from "@legendapp/state/react";
import type { ChatMode } from "@repo/shared/types/chat-mode";
import { ChatModeSchema } from "@repo/shared/types/chat-mode";
import type { BaseIconName } from "@repo/ui/components/icon";
import { Icon } from "@repo/ui/components/icon";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@repo/ui/dropdown-menu";
import { InputGroupButton } from "@repo/ui/input-group";
import { ShortcutKbd } from "@repo/ui/lib/actions/shortcut-kbd";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/ui/tooltip";

import { chat$ } from "~/stores/chat";

export const CHAT_MODE_LABELS: Record<ChatMode, string> = {
  AUTO: "Auto",
  PLAN: "Plan",
  DASHBOARD: "Dashboard",
  ASK: "Ask",
};

export const CHAT_MODE_ICONS: Record<ChatMode, BaseIconName> = {
  AUTO: "stars",
  PLAN: "clipboard",
  DASHBOARD: "data-line-chart",
  ASK: "comment",
};

/** Dropdown to select the AI chat mode: Auto, Plan, Dashboard, or Ask. */
export const ChatModeSelector = () => {
  const value = useValue(chat$.mode);
  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger
          render={
            <DropdownMenuTrigger
              render={
                <InputGroupButton
                  size="xs"
                  variant="secondary"
                  className="h-6 gap-1 rounded-full pl-1.5"
                >
                  <Icon name={CHAT_MODE_ICONS[value]} className="size-3" />
                  <span className="text-muted-foreground text-xs">
                    {CHAT_MODE_LABELS[value]}
                  </span>
                  <Icon name="chevron-down" className="size-3.5 opacity-60" />
                </InputGroupButton>
              }
            />
          }
        />
        <TooltipContent>
          {CHAT_MODE_LABELS[value]} mode{" "}
          <ShortcutKbd shortcutId="cycle-chat-mode" />
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="start" side="top">
        <DropdownMenuRadioGroup
          value={value}
          onValueChange={(v) => chat$.mode.set(v as ChatMode)}
        >
          {ChatModeSchema.options.map((mode) => (
            <DropdownMenuRadioItem key={mode} value={mode}>
              <Icon name={CHAT_MODE_ICONS[mode]} />
              {CHAT_MODE_LABELS[mode]}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
